import useIsMobile from "./useIsMobile";

const TEAM = [
  { id: "BH", name: "Ben", role: "Product Manager", color: "#f97316" },
  { id: "JC", name: "Justis", role: "Engineer", color: "#06b6d4" },
  { id: "KL", name: "Keith", role: "Editor in Chief", color: "#ec4899" },
  { id: "BE", name: "Blair", role: "Editor", color: "#a78bfa" },
  { id: "SB", name: "Sinead", role: "CEO", color: "#facc15" },
];

const DAYS = [
  {
    day: 1,
    date: "Tue 24 Mar",
    title: "Bold Ideation",
    color: "#3b82f6",
    attendees: ["BH", "JC", "KL", "BE", "SB"],
    activities: [
      "Think boldly — draw out the ideal end-state platform",
      "Ideate with CEO on what the platform could become",
      "No constraints thinking: what does world-class look like?",
      "Capture aspirational product vision",
    ],
  },
  {
    day: 2,
    date: "Wed 25 Mar",
    title: "TV Package",
    color: "#8b5cf6",
    attendees: ["BH", "JC", "KL", "BE"],
    activities: [
      "Work on what it takes to make a TV package",
      "Map the end-to-end workflow from story to broadcast",
      "Identify tooling and integration requirements",
      "Define quality benchmarks and turnaround targets",
    ],
  },
  {
    day: 3,
    date: "Thu 26 Mar",
    title: "Map Architecture",
    color: "#06b6d4",
    attendees: ["BH", "JC", "KL", "BE"],
    activities: [
      "Map out technical architecture and system design",
      "Define components, integrations, and data flows",
      "Identify build-vs-buy decisions",
      "Draft technical requirements and constraints",
    ],
  },
  {
    day: 4,
    date: "Fri 27 Mar",
    title: "Sprint Planning",
    color: "#10b981",
    attendees: ["BH", "JC", "KL", "BE"],
    activities: [
      "Plan the first sprint based on kickoff outcomes",
      "Assign workstreams for remote collaboration",
      "Define success criteria and deliverables",
      "Set up cadence and communication norms",
    ],
  },
];

const BUSINESSES = [
  {
    name: "Stuff Digital",
    color: "#3b82f6",
    outcomes: [
      {
        tag: "EBITDA",
        tagColor: "#10b981",
        title: "Reduce cost per published piece",
        description:
          "Exit circa $200k Naviga licensing cost (and business continuity risk) and produce more high-quality journalism in a wider range of formats without increasing FTE investment.",
        kpis: [
          "Increase publishable story volume per journalist",
          "Increase editorial acceptance rate",
          "Increase percentage of content published via AI Production Engine",
        ],
      },
      {
        tag: "AUDIENCE",
        tagColor: "#8b5cf6",
        title: "Enable consumer product innovation that grows audience",
        description:
          "Eliminate the constraint current editorial production workflows place on our ability to provide news experiences that keep pace with quickly changing consumer trends.",
        kpis: [
          "Increase daily percentage of stories with video",
          "Increase daily percentage of stories with audio",
          "Increase daily vertical video volume",
          "Reduce time to publish",
        ],
      },
      {
        tag: "REVENUE",
        tagColor: "#f59e0b",
        title: "Retain ThreeNews production revenue",
        description:
          "Meaningfully reduce the cost to produce ThreeNews allowing us to strengthen quality and quantity of our service to Sky while increasing contract profitability.",
        kpis: [
          "Increase daily percentage of ThreeNews stories produced by AI Production Engine",
        ],
      },
    ],
  },
  {
    name: "Stuff Masthead Publishing",
    color: "#8b5cf6",
    outcomes: [
      {
        tag: "EBITDA",
        tagColor: "#10b981",
        title: "Reduce cost per published piece",
        description:
          "Exit circa $533k Naviga licensing cost (and business continuity risk) and produce more high-quality journalism in a wider range of formats without increasing FTE investment.",
        kpis: [
          "Increase publishable story volume per journalist",
          "Increase editorial acceptance rate",
          "Increase percentage of content published via AI Production Engine",
        ],
      },
      {
        tag: "EBITDA",
        tagColor: "#10b981",
        title: "Eliminate print production overhead",
        description:
          "Facilitate the full automation of print production, unlocking investment capacity for digital growth.",
        kpis: [
          "Increase percentage of print pages published via AI Production Engine",
        ],
      },
      {
        tag: "REVENUE",
        tagColor: "#f59e0b",
        title: "Enable consumer product innovation that grows subscriptions",
        description:
          "Eliminate the constraint current editorial production workflows place on our ability to provide news experiences that keep pace with quickly changing consumer trends.",
        kpis: [
          "Increase daily percentage of stories with video",
          "Increase daily percentage of stories with audio",
          "Reduce time to publish",
        ],
      },
    ],
  },
];

const QUESTIONS = [
  {
    tag: "CONTENT",
    tagColor: "#3b82f6",
    title: "Partner Content Ingestion",
    description:
      "How do we handle AP content and other partner feeds? What does resizing and reformatting for VV (vertical video) look like in an automated pipeline?",
  },
  {
    tag: "PLATFORM",
    tagColor: "#8b5cf6",
    title: "Site Element Plugins",
    description:
      "How does the engine plug into other site elements — comment moderation, quizzes, polls, and storytelling devices? What's the integration surface?",
  },
  {
    tag: "EDITORIAL",
    tagColor: "#ec4899",
    title: "Human Element & Timing",
    description:
      "Where does the human sit in the loop? First take vs chatbot-style interaction. How do we pull context from the internet and back catalogue effectively?",
  },
  {
    tag: "TOOLING",
    tagColor: "#f59e0b",
    title: "Editor Backend",
    description:
      "What does the editor-facing backend look like? How do editors manage, review, and control AI-assisted production workflows day to day?",
  },
  {
    tag: "EDITORIAL",
    tagColor: "#ec4899",
    title: "Bylines",
    description:
      'How do we frame attribution for AI-assisted content? e.g. "Reporting by Keith Lynch" — what\'s the right model for transparency and trust?',
  },
];

function Badge({ id }) {
  const person = TEAM.find((t) => t.id === id);
  if (!person) return null;
  return (
    <span
      title={`${person.name} · ${person.role}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 26,
        height: 26,
        borderRadius: "50%",
        background: person.color + "22",
        color: person.color,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: "'IBM Plex Mono', monospace",
        border: `1.5px solid ${person.color}55`,
        cursor: "default",
      }}
    >
      {person.id}
    </span>
  );
}

export default function Kickoff() {
  const mob = useIsMobile();

  return (
    <div
      style={{
        padding: mob ? "20px 16px 40px" : "32px 48px 64px",
        maxWidth: 1200,
        margin: "0 auto",
        fontFamily: "'IBM Plex Sans', sans-serif",
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: mob ? 20 : 32 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#475569",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            marginBottom: 8,
            fontFamily: "'IBM Plex Mono', monospace",
          }}
        >
          Christchurch Kickoff
        </div>
        <div
          style={{
            fontSize: mob ? 20 : 24,
            fontWeight: 700,
            color: "#e2e8f0",
            marginBottom: 6,
          }}
        >
          March 24–27, 2026
        </div>
        <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.6, maxWidth: 640 }}>
          Four days together in Christchurch to kick off the AI Production Engine.
          The team comes together to ideate, prototype, architect, and plan the
          first sprint.
        </div>
      </div>

      {/* Team legend */}
      <div
        style={{
          display: "flex",
          gap: mob ? 10 : 16,
          marginBottom: mob ? 20 : 28,
          flexWrap: "wrap",
        }}
      >
        {TEAM.map((t) => (
          <div
            key={t.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 12,
              color: "#64748b",
            }}
          >
            <Badge id={t.id} />
            {!mob && (
              <span>
                {t.name}{" "}
                <span style={{ color: "#334155" }}>· {t.role}</span>
              </span>
            )}
          </div>
        ))}
      </div>

      {/* 4-Day Plan */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: mob ? "1fr" : "repeat(4, 1fr)",
          gap: 12,
          marginBottom: mob ? 32 : 48,
        }}
      >
        {DAYS.map((d) => (
          <div
            key={d.day}
            style={{
              background: "#0f1623",
              border: "1px solid #1e2d42",
              borderRadius: 8,
              padding: mob ? "14px 16px" : "18px 20px",
              borderTop: `3px solid ${d.color}`,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Day header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: d.color,
                  fontFamily: "'IBM Plex Mono', monospace",
                  letterSpacing: "0.06em",
                }}
              >
                DAY {d.day}
              </span>
              <span
                style={{
                  fontSize: 11,
                  color: "#475569",
                  fontFamily: "'IBM Plex Mono', monospace",
                }}
              >
                {d.date}
              </span>
            </div>

            {/* Title */}
            <div
              style={{
                fontSize: 17,
                fontWeight: 700,
                color: "#e2e8f0",
                marginBottom: 14,
                lineHeight: 1.3,
              }}
            >
              {d.title}
            </div>

            {/* Attendees */}
            <div
              style={{
                display: "flex",
                gap: 5,
                marginBottom: 16,
              }}
            >
              {d.attendees.map((a) => (
                <Badge key={a} id={a} />
              ))}
            </div>

            {/* Activities */}
            <div style={{ flex: 1 }}>
              {d.activities.map((act, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: 8,
                    marginBottom: 8,
                    fontSize: 12,
                    color: "#94a3b8",
                    lineHeight: 1.5,
                  }}
                >
                  <span
                    style={{
                      color: d.color + "88",
                      flexShrink: 0,
                      marginTop: 1,
                      fontSize: 9,
                    }}
                  >
                    ●
                  </span>
                  {act}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Develop Once, Deploy Twice */}
      <div style={{ borderTop: "1px solid #1e2d42", paddingTop: 24, marginBottom: mob ? 32 : 48 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#475569",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            marginBottom: 8,
            fontFamily: "'IBM Plex Mono', monospace",
          }}
        >
          Strategic Principle
        </div>
        <div
          style={{
            fontSize: mob ? 18 : 20,
            fontWeight: 700,
            color: "#e2e8f0",
            marginBottom: 6,
          }}
        >
          Develop Once, Deploy Twice
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#64748b",
            marginBottom: 20,
            lineHeight: 1.6,
            maxWidth: 640,
          }}
        >
          We build the AI Production Engine once and deploy it to each business.
          Each business applies its own content "recipe" and can iterate
          independently for competitive advantage over time.
        </div>

        <div
          style={{
            fontSize: mob ? 18 : 20,
            fontWeight: 700,
            color: "#e2e8f0",
            marginBottom: 6,
          }}
        >
          Stand Up Alongside, Then Replace
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#64748b",
            marginBottom: 20,
            lineHeight: 1.6,
            maxWidth: 640,
          }}
        >
          We stand the AI Production Engine up alongside Naviga and quickly make
          it the preferred way of producing content. No hard cutover — instead,
          adoption grows naturally as the new system proves itself, until Naviga
          is no longer needed.
        </div>

      </div>

      {/* Business Outcomes & Product KPIs */}
      <div style={{ borderTop: "1px solid #1e2d42", paddingTop: 24, marginBottom: mob ? 32 : 48 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#475569",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            marginBottom: 8,
            fontFamily: "'IBM Plex Mono', monospace",
          }}
        >
          Business Outcomes &amp; Product KPIs
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#475569",
            marginBottom: 20,
            lineHeight: 1.6,
          }}
        >
          What the product must achieve in order to deliver each business
          outcome. Baselines to be established at project commencement.
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {BUSINESSES.map((biz) => (
            <div key={biz.name}>
              {/* Business name */}
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: biz.color,
                  marginBottom: 12,
                  fontFamily: "'IBM Plex Mono', monospace",
                  letterSpacing: "0.04em",
                }}
              >
                {biz.name}
              </div>

              {/* Outcomes grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: mob
                    ? "1fr"
                    : `repeat(${biz.outcomes.length}, 1fr)`,
                  gap: 12,
                }}
              >
                {biz.outcomes.map((o, i) => (
                  <div
                    key={i}
                    style={{
                      padding: "16px 18px",
                      background: "#0f1623",
                      borderRadius: 8,
                      border: "1px solid #1e2d42",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Tag */}
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        padding: "2px 7px",
                        borderRadius: 3,
                        color: o.tagColor,
                        background: o.tagColor + "18",
                        textTransform: "uppercase",
                        alignSelf: "flex-start",
                      }}
                    >
                      {o.tag}
                    </span>

                    {/* Outcome title */}
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#e2e8f0",
                        marginTop: 10,
                        marginBottom: 8,
                        lineHeight: 1.4,
                      }}
                    >
                      {o.title}
                    </div>

                    {/* Outcome description */}
                    <div
                      style={{
                        fontSize: 12,
                        color: "#64748b",
                        lineHeight: 1.6,
                        marginBottom: 14,
                        flex: 1,
                      }}
                    >
                      {o.description}
                    </div>

                    {/* KPIs */}
                    <div
                      style={{
                        borderTop: "1px solid #1e2d42",
                        paddingTop: 10,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          marginBottom: 8,
                          fontFamily: "'IBM Plex Mono', monospace",
                        }}
                      >
                        Product KPIs
                      </div>
                      {o.kpis.map((kpi, k) => (
                        <div
                          key={k}
                          style={{
                            display: "flex",
                            gap: 8,
                            marginBottom: 6,
                            fontSize: 12,
                            color: "#94a3b8",
                            lineHeight: 1.5,
                          }}
                        >
                          <span
                            style={{
                              color: o.tagColor + "88",
                              flexShrink: 0,
                              marginTop: 1,
                              fontSize: 9,
                            }}
                          >
                            ●
                          </span>
                          {kpi}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Open Questions */}
      <div style={{ borderTop: "1px solid #1e2d42", paddingTop: 24 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#475569",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            marginBottom: 8,
            fontFamily: "'IBM Plex Mono', monospace",
          }}
        >
          Open Questions &amp; Considerations
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#475569",
            marginBottom: 20,
            lineHeight: 1.6,
          }}
        >
          Key questions raised by the team to address during kickoff and early
          sprints.
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: mob ? "1fr" : "1fr 1fr 1fr",
            gap: 12,
          }}
        >
          {QUESTIONS.map((q) => (
            <div
              key={q.title}
              style={{
                padding: "16px 18px",
                background: "#0f1623",
                borderRadius: 8,
                border: "1px solid #1e2d42",
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  padding: "2px 7px",
                  borderRadius: 3,
                  color: q.tagColor,
                  background: q.tagColor + "18",
                  textTransform: "uppercase",
                }}
              >
                {q.tag}
              </span>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#e2e8f0",
                  marginTop: 10,
                  marginBottom: 8,
                  lineHeight: 1.4,
                }}
              >
                {q.title}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#64748b",
                  lineHeight: 1.6,
                }}
              >
                {q.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
